import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, MessageCircle, Phone } from "lucide-react";
import { EnquiryModal } from "./EnquiryModal";
import { waLink, PHONE } from "../lib/api";

const NAV = [
  { to: "/courses", label: "Courses" },
  { to: "/about", label: "About" },
  { to: "/services", label: "Services" },
  { to: "/blog", label: "Blog" },
  { to: "/careers", label: "Careers" },
  { to: "/contact", label: "Contact" },
];

export const Logo = ({ light = false }) => (
  <Link to="/" className="flex items-center gap-2.5" data-testid="logo-link">
    <div className="w-10 h-10 bg-[#0A192F] rounded-lg flex items-center justify-center border border-[#C59B27]/70 shrink-0">
      <span className="text-[#C59B27] font-display font-extrabold text-sm tracking-tight">SN</span>
    </div>
    <div className="leading-none">
      <span className={`font-display font-extrabold text-lg tracking-tight ${light ? "text-white" : "text-[#0A192F]"}`}>
        Sterling Nest
      </span>
      <span className="block text-[9px] font-mono-sn font-semibold tracking-[0.28em] text-[#0F5132] mt-1">
        LEARN. PRACTICE. GROW.
      </span>
    </div>
  </Link>
);

export const Header = () => {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-50 backdrop-blur-xl bg-white/90 border-b border-slate-200/80 shadow-[0_1px_12px_rgba(10,25,47,0.06)]"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-[72px] flex items-center justify-between gap-4">
        <Logo />
        <nav className="hidden lg:flex items-center gap-7" data-testid="desktop-nav">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.label.toLowerCase()}`}
              className={({ isActive }) =>
                `text-sm font-semibold transition-colors ${
                  isActive ? "text-[#0F5132]" : "text-[#334E68] hover:text-[#0A192F]"
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="hidden lg:flex items-center gap-3">
          <a
            href={`tel:+91${PHONE.replace(/\D/g, "").slice(-10)}`}
            data-testid="header-phone-link"
            className="flex items-center gap-1.5 text-sm font-semibold text-[#0A192F] hover:text-[#0F5132] transition-colors"
          >
            <Phone size={15} className="text-[#0F5132]" /> {PHONE}
          </a>
          <a
            href={waLink()}
            target="_blank"
            rel="noreferrer"
            data-testid="header-whatsapp-btn"
            className="flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-[#25D366] text-white text-sm font-semibold hover:bg-[#1fb857] transition-colors"
          >
            <MessageCircle size={15} /> WhatsApp
          </a>
          <EnquiryModal
            source="header"
            trigger={
              <button
                data-testid="header-enquire-btn"
                className="px-5 py-2 rounded-full bg-[#0A192F] text-white text-sm font-semibold hover:bg-[#0F5132] transition-colors"
              >
                Enquire Now
              </button>
            }
          />
        </div>
        <button
          className="lg:hidden p-2 text-[#0A192F]"
          onClick={() => setOpen(!open)}
          data-testid="mobile-menu-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>
      {open && (
        <div className="lg:hidden border-t border-slate-200 bg-white px-4 py-4 space-y-1" data-testid="mobile-menu">
          {NAV.map((n) => (
            <Link
              key={n.to}
              to={n.to}
              onClick={() => setOpen(false)}
              data-testid={`mobile-nav-${n.label.toLowerCase()}`}
              className={`block px-3 py-2.5 rounded-lg text-sm font-semibold ${
                location.pathname.startsWith(n.to) ? "bg-[#E8F5E9] text-[#0F5132]" : "text-[#334E68]"
              }`}
            >
              {n.label}
            </Link>
          ))}
          <div className="flex gap-2 pt-3">
            <a
              href={waLink()}
              target="_blank"
              rel="noreferrer"
              data-testid="mobile-whatsapp-btn"
              className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-full bg-[#25D366] text-white text-sm font-semibold"
            >
              <MessageCircle size={15} /> WhatsApp
            </a>
            <EnquiryModal
              source="header-mobile"
              trigger={
                <button
                  data-testid="mobile-enquire-btn"
                  className="flex-1 px-3 py-2.5 rounded-full bg-[#0A192F] text-white text-sm font-semibold"
                >
                  Enquire Now
                </button>
              }
            />
          </div>
        </div>
      )}
    </header>
  );
};
